// V2 shell — completely different layout from V1.
//
// V1 (Shell.jsx) layout:
//   ┌────────────────────────┬──────┐
//   │ topbar                 │      │
//   ├────────────────────────┤ side │
//   │ page content           │ nav  │
//   └────────────────────────┴──────┘
//
// V2 (this file) layout:
//   ┌──────────────────────────────┐
//   │ logo · tabs · controls · me  │   horizontal app bar
//   ├──────────────────────────────┤
//   │ Dashboard / <active page>    │   breadcrumb strip
//   ├──────────────────────────────┤
//   │                              │
//   │      page content (full)     │
//   │                              │
//   └──────────────────────────────┘
//
// No side rail. Nav modules live as tabs across the top, with overflow
// captured by a "More" dropdown on narrow viewports. Same Module + views
// render inside; no behavior changes.

import React, { useEffect, useState, useCallback, useRef } from "react";
import { LogOut, Menu, Search, Shield, X, Sun, Moon, ChevronDown } from "lucide-react";
import { loadState, logout } from "../api";
import { nav, can } from "../nav";
import Module from "./Module";
import WelcomeOverlay from "./WelcomeOverlay";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";
import { ActiveTeamProvider } from "./ActiveTeam";
import TeamSwitcher from "./TeamSwitcher";
import { useUnreadMessages } from "./useUnreadMessages";
import { useAutoReload } from "./useAutoReload";
import NotificationBell from "./NotificationBell";
import ChatFab from "./ChatFab";

const EMPTY_STATE = {
  members: [], groups: [], teams: [], matches: [], availability: [],
  squads: [], announcements: [], transactions: [], notifications: []
};

// Visible-tab budget — anything past index N goes under the "More" dropdown.
const VISIBLE_TABS = 6;

export default function ShellV2({ user, setUser }) {
  const [state, setState]   = useState(EMPTY_STATE);
  const [active, setActive] = useState("dashboard");
  // Optional payload carried alongside a navigation (see Shell.jsx).
  const [navArg, setNavArg] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreOpen, setMoreOpen]     = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  function navigate(key, arg = null) {
    setActive(key);
    setNavArg(arg);
  }
  const { theme, toggleTheme } = useTheme();
  const unreadMessages = useUnreadMessages({ active, onNavigate: setActive });
  const { version, setVersion } = useVersion();
  const moreRef = useRef(null);

  const [showWelcome, setShowWelcome] = useState(
    () => sessionStorage.getItem("gabruz-just-logged-in") === "1"
  );
  useEffect(() => {
    if (showWelcome) sessionStorage.removeItem("gabruz-just-logged-in");
  }, [showWelcome]);

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadState();
      setState(next);
    } catch (err) {
      if (err.status === 401) { setUser(null); return; }
      setError(err.message || "Failed to load data");
    }
  }, [setUser]);

  // Cross-device sync — see useAutoReload for details.
  useAutoReload(reload);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await reload();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [reload]);

  // Close "More" dropdown on outside click / Esc.
  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMoreOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [moreOpen]);

  const allowed = nav.filter(n => can(user, n.key));
  const visibleTabs = allowed.slice(0, VISIBLE_TABS);
  const overflowTabs = allowed.slice(VISIBLE_TABS);
  const activeItem = allowed.find(n => n.key === active);

  function signOut() {
    logout();
    setUser(null);
  }

  function navigateTo(key) {
    setActive(key);
    setDrawerOpen(false);
    setMoreOpen(false);
  }

  const initial = (user.name || "?")[0]?.toUpperCase() || "?";

  return (
    <ActiveTeamProvider user={user} teams={state.teams}>
    <div className={`appV2${showWelcome ? " app--welcoming" : ""}`}>
      {showWelcome && <WelcomeOverlay onDone={() => setShowWelcome(false)} />}

      <header className="appV2-bar">
        {/* Brand */}
        <a className="appV2-brand" href="#" onClick={(e) => { e.preventDefault(); navigateTo("dashboard"); }}>
          <img src="/gabruz-brand.png" onError={e => e.currentTarget.style.display = "none"} alt="Gabruz Universe" />
          <span className="appV2-brand-name">Gabruz</span>
          <span className="appV2-brand-sub">Universe</span>
        </a>
        <NotificationBell notifications={state.notifications} onNavigate={navigateTo}/>

        {/* Tab nav (visible on ≥md) */}
        <nav className="appV2-tabs" aria-label="Main">
          {visibleTabs.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                className={active === item.key ? "appV2-tab appV2-tab--active" : "appV2-tab"}
                onClick={() => navigateTo(item.key)}
              >
                <Icon size={15} /><span>{item.label}</span>
              </button>
            );
          })}
          {overflowTabs.length > 0 && (
            <div className="appV2-more" ref={moreRef}>
              <button
                type="button"
                className={overflowTabs.some(t => t.key === active) ? "appV2-tab appV2-tab--active" : "appV2-tab"}
                onClick={() => setMoreOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={moreOpen}
              >
                More <ChevronDown size={14} />
              </button>
              {moreOpen && (
                <div className="appV2-more-menu" role="menu">
                  {overflowTabs.map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className={active === item.key ? "appV2-more-item appV2-more-item--active" : "appV2-more-item"}
                        onClick={() => navigateTo(item.key)}
                        role="menuitem"
                      >
                        <Icon size={15} /><span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Right cluster: team · search · version · theme · profile */}
        <div className="appV2-cluster">
          <TeamSwitcher />
          <div className="appV2-search">
            <Search size={15} />
            <input placeholder="Search…" />
          </div>

          <div className="versionSwitch" role="radiogroup" aria-label="UI design version">
            <button type="button" className={version === "v1" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v1")} role="radio" aria-checked={version === "v1"} title="Original UI">V1</button>
            <button type="button" className={version === "v2" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v2")} role="radio" aria-checked={version === "v2"} title="Aurora UI">V2</button>
            <button type="button" className={version === "v3" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v3")} role="radio" aria-checked={version === "v3"} title="Bento UI">V3</button>
            <button type="button" className={version === "v4" ? "versionSwitch-btn versionSwitch-btn--active" : "versionSwitch-btn"} onClick={() => setVersion("v4")} role="radio" aria-checked={version === "v4"} title="Mobile UI">V4</button>
          </div>

          <button type="button" className="themeToggle" onClick={toggleTheme} title={theme === "light" ? "Switch to dark theme" : "Switch to light theme"} aria-label="Toggle color theme">
            {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </button>

          <div className="appV2-me">
            <div className="appV2-me-avatar">{user.avatar || initial}</div>
            <div className="appV2-me-text">
              <strong>{user.name}</strong>
              <span><Shield size={11} /> {user.roles[0].replace("_", " ")}</span>
            </div>
            <button type="button" className="appV2-me-out" onClick={signOut} title="Sign out" aria-label="Sign out">
              <LogOut size={15} />
            </button>
          </div>

          <button className="appV2-drawer-btn" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
            <Menu size={18} />
          </button>
        </div>
      </header>

      <div className="appV2-crumb">
        <span className="appV2-crumb-trail">Gabruz Universe / </span>
        <span className="appV2-crumb-active">{activeItem?.label || "Dashboard"}</span>
      </div>

      <main className="appV2-main">
        <div className="page page--v2">
          {loading ? (
            <div className="empty">
              <h2>Loading…</h2>
              <p>Fetching data from the API.</p>
            </div>
          ) : error ? (
            <div className="empty">
              <h2>Couldn't load data</h2>
              <p>{error}</p>
              <button className="primary" onClick={reload}>Retry</button>
            </div>
          ) : (
            <Module active={active} setActive={navigate} navArg={navArg} state={state} reload={reload} user={user} setUser={setUser} />
          )}
        </div>
      </main>

      {/* Mobile slide-in drawer (only used on narrow viewports) */}
      {drawerOpen && (
        <>
          <div className="appV2-drawer-veil" onClick={() => setDrawerOpen(false)} />
          <aside className="appV2-drawer">
            <div className="appV2-drawer-head">
              <strong>Navigation</strong>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close menu"><X size={18}/></button>
            </div>
            {allowed.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={active === item.key ? "appV2-drawer-link appV2-drawer-link--active" : "appV2-drawer-link"}
                  onClick={() => navigateTo(item.key)}
                >
                  <Icon size={16} /><span>{item.label}</span>
                </button>
              );
            })}
          </aside>
        </>
      )}
      <ChatFab active={active} unread={unreadMessages} onOpen={() => setActive("messages")} />
    </div>
    </ActiveTeamProvider>
  );
}
